/**
 * UnifiedTable - Consolidated table component that replaces AdvancedTable, SimpleAdvancedTable, and HorizontalTable
 * Combines all features while maintaining the Notion-style UI principles
 * 
 * Key Features:
 * - Notion-style column menus and filtering
 * - Advanced sorting with multi-column support
 * - Global search and column-specific filtering
 * - Resizable and sticky columns
 * - Pagination with state persistence
 * - Export functionality
 * - Loading and error states
 * - Responsive design
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  Search, 
  X, 
  Filter, 
  Download, 
  RefreshCw, 
  ArrowUpDown,
  Eye,
  EyeOff,
  MoreHorizontal 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';

import StickyTableHeader from '../StickyTableHeader';
import PaginationControls from '../PaginationControls';
import { EnhancedTableHeader, EnhancedTableColumn } from './EnhancedTableHeader';
import { NotionColumnMenu } from './NotionColumnMenu';
import { 
  FilterState, 
  FilterValue,
  createInitialFilterState,
  applyFilters 
} from '../../../utils/tableFilters';
import { 
  SortState, 
  SortDirection,
  createInitialSortState,
  applySorting 
} from '../../../utils/tableSort';
import { 
  PaginationState, 
  createPaginationState, 
  paginateData 
} from '../../../utils/pagination-utils';

// Types
export type CellRenderer<T = any> = (
  value: any, 
  column: EnhancedTableColumn, 
  row: T, 
  rowIndex: number
) => React.ReactNode;

export interface TableStateConfig {
  persistenceKey?: string;
  persistToLocalStorage?: boolean;
  pageSize?: number;
  defaultSortColumn?: string;
  defaultSortDirection?: SortDirection;
}

export interface UnifiedTableProps<T extends Record<string, any> = any> {
  // Core data
  data: T[];
  columns: EnhancedTableColumn[];
  
  // Configuration
  tableStateConfig?: TableStateConfig;
  
  // Feature toggles
  enableFiltering?: boolean;
  enableSorting?: boolean;
  enableGlobalSearch?: boolean;
  enableColumnVisibility?: boolean;
  enableExport?: boolean;
  enableRefresh?: boolean;
  enableNotionStyle?: boolean; // Use Notion-style column menus
  
  // UI customization
  title?: string;
  subtitle?: string;
  description?: string;
  showToolbar?: boolean;
  toolbarActions?: React.ReactNode;
  compactMode?: boolean;
  stickyHeader?: boolean;
  
  // Styling
  className?: string;
  headerClassName?: string;
  rowClassName?: string | ((row: T, index: number) => string);
  
  // Event handlers
  onRowClick?: (row: T, index: number) => void;
  onExport?: (data: T[]) => void;
  onRefresh?: () => void;
  onDataChange?: (filteredData: T[], sortedData: T[], paginatedData: T[]) => void;
  cellRenderer?: CellRenderer<T>;
  
  // States
  isLoading?: boolean;
  isRefreshing?: boolean;
  error?: string | null;
  onRetry?: () => void;
  
  // Custom content
  emptyState?: React.ReactNode;
  loadingRows?: number;
}

/**
 * Default cell renderer with smart formatting
 */
const defaultCellRenderer: CellRenderer = (value, column, row, rowIndex) => {
  // Handle URLs
  if (column.key === 'url' && value) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary hover:text-primary/80 underline break-all whitespace-normal block"
      >
        {value}
      </a>
    );
  }

  // Handle long text content
  if ((column.key === 'description' || column.key === 'answer') && value) {
    return (
      <div className="max-w-md text-foreground break-words whitespace-normal">
        {value}
      </div>
    );
  }

  // Handle numbers
  if (typeof value === 'number') {
    return (
      <span className="text-foreground font-mono">
        {value.toLocaleString()}
      </span>
    );
  }

  // Handle dates
  if (column.key.includes('date') && value) {
    try {
      const date = new Date(value);
      return (
        <span className="text-foreground">
          {date.toLocaleDateString()}
        </span>
      );
    } catch {
      // Fall through to default
    }
  }

  // Default handling
  return (
    <span className="text-foreground">
      {value || (
        <span className="text-muted-foreground text-xs">—</span>
      )}
    </span>
  );
};

/**
 * Unified Table Component
 */
export function UnifiedTable<T extends Record<string, any> = any>({
  data,
  columns,
  tableStateConfig,
  enableFiltering = true,
  enableSorting = true,
  enableGlobalSearch = true,
  enableColumnVisibility = true,
  enableExport = false,
  enableRefresh = false,
  enableNotionStyle = true,
  title,
  subtitle,
  description,
  showToolbar = true,
  toolbarActions,
  compactMode = false,
  stickyHeader = true,
  className,
  headerClassName,
  rowClassName,
  onRowClick,
  onExport,
  onRefresh,
  onDataChange,
  cellRenderer = defaultCellRenderer,
  isLoading = false,
  isRefreshing = false,
  error = null,
  onRetry,
  emptyState,
  loadingRows = 5,
}: UnifiedTableProps<T>) {
  
  // State management
  const [filterState, setFilterState] = useState<FilterState>(() => 
    createInitialFilterState()
  );
  
  const [sortState, setSortState] = useState<SortState>(() => 
    createInitialSortState(
      tableStateConfig?.defaultSortColumn,
      tableStateConfig?.defaultSortDirection
    )
  );
  
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    columns.forEach(col => {
      initial[col.key] = true;
    });
    return initial;
  });
  
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    columns.forEach(col => {
      if (col.width) {
        initial[col.key] = col.width;
      }
    });
    return initial;
  });
  
  const [globalSearchValue, setGlobalSearchValue] = useState('');
  const [paginationState, setPaginationState] = useState<PaginationState>(() =>
    createPaginationState({ pageSize: tableStateConfig?.pageSize || 20 })
  );

  // Process data through filtering, sorting, and pagination
  const processedData = useMemo(() => {
    let result = [...data];
    
    // Apply global search
    if (globalSearchValue && enableGlobalSearch) {
      const searchLower = globalSearchValue.toLowerCase();
      result = result.filter(row => 
        Object.values(row).some(value => 
          String(value).toLowerCase().includes(searchLower)
        )
      );
    }
    
    // Apply column filters
    if (enableFiltering) {
      result = applyFilters(result, filterState);
    }
    
    return result;
  }, [data, globalSearchValue, filterState, enableGlobalSearch, enableFiltering]);

  // Apply sorting
  const sortedData = useMemo(() => {
    if (!enableSorting || sortState.columns.length === 0) {
      return processedData;
    }
    
    return applySorting(processedData, sortState, columns);
  }, [processedData, sortState, columns, enableSorting]);

  // Apply pagination
  const paginatedData = useMemo(() => {
    return paginateData(sortedData, paginationState);
  }, [sortedData, paginationState]);

  // Update pagination when data changes
  useEffect(() => {
    setPaginationState(createPaginationState({ 
      totalItems: sortedData.length,
      pageSize: paginationState.pageSize,
      currentPage: 1 // Reset to first page when data changes
    }));
  }, [sortedData.length]);

  // Notify parent of data changes
  useEffect(() => {
    if (onDataChange) {
      onDataChange(processedData, sortedData, paginatedData);
    }
  }, [processedData, sortedData, paginatedData, onDataChange]);

  // Get visible columns
  const visibleColumns = useMemo(() => {
    return columns.filter(col => columnVisibility[col.key] !== false);
  }, [columns, columnVisibility]);

  // Event handlers
  const handleColumnResize = useCallback((columnKey: string, newWidth: string) => {
    setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
  }, []);

  const handleFilterChange = useCallback((columnKey: string, filter: FilterValue | null) => {
    setFilterState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [columnKey]: filter ? { value: filter, columnKey, type: 'text' } : undefined
      }
    }));
  }, []);

  const handleSortChange = useCallback((columnKey: string, direction: SortDirection) => {
    setSortState(prev => ({
      ...prev,
      columns: direction ? [{
        columnKey,
        direction,
        priority: 1
      }] : prev.columns.filter(col => col.columnKey !== columnKey)
    }));
  }, []);

  const handleColumnVisibilityChange = useCallback((columnKey: string, visible: boolean) => {
    setColumnVisibility(prev => ({ ...prev, [columnKey]: visible }));
  }, []);

  const handleGlobalSearchChange = useCallback((value: string) => {
    setGlobalSearchValue(value);
  }, []);

  const handleExport = useCallback(() => {
    if (onExport) {
      onExport(sortedData); // Export all filtered/sorted data, not just current page
    }
  }, [onExport, sortedData]);

  const handleClearAllFilters = useCallback(() => {
    setFilterState(createInitialFilterState());
    setGlobalSearchValue('');
  }, []);

  const handleClearAllSorts = useCallback(() => {
    setSortState(createInitialSortState());
  }, []);

  // Get row class name
  const getRowClassName = useCallback((row: T, index: number) => {
    const baseClass = "border-b border-border/20 hover:bg-muted/20 transition-colors duration-150";
    if (typeof rowClassName === 'function') {
      return `${baseClass} ${rowClassName(row, index)}`;
    }
    return `${baseClass} ${rowClassName || ''}`;
  }, [rowClassName]);

  // Render toolbar
  const renderToolbar = () => {
    if (!showToolbar) return null;

    const hasActiveFilters = Object.values(filterState.filters).some(f => f !== undefined) || globalSearchValue;
    const hasActiveSorts = sortState.columns.length > 0;

    return (
      <div className="flex flex-col gap-4 px-6 py-4 border-b border-border/30">
        {/* Title and actions */}
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            {title && (
              <h2 className="text-xl font-medium text-foreground truncate">
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-sm text-muted-foreground truncate mt-1">
                {subtitle}
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2 ml-4">
            {toolbarActions}
            
            {/* Column visibility menu */}
            {enableColumnVisibility && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  >
                    <Eye className="h-4 w-4 mr-1.5" />
                    Columns
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {columns.map(column => (
                    <DropdownMenuCheckboxItem
                      key={column.key}
                      checked={columnVisibility[column.key] !== false}
                      onCheckedChange={(checked) => handleColumnVisibilityChange(column.key, checked)}
                    >
                      {column.label}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {/* Refresh button */}
            {enableRefresh && onRefresh && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="text-muted-foreground hover:text-foreground hover:bg-muted/50"
              >
                <RefreshCw className={cn("h-4 w-4 mr-1.5", isRefreshing && "animate-spin")} />
                Refresh
              </Button>
            )}
            
            {/* Export button */}
            {enableExport && onExport && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleExport}
                disabled={sortedData.length === 0}
                className="text-muted-foreground hover:text-foreground hover:bg-muted/50"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Export ({sortedData.length})
              </Button>
            )}
          </div>
        </div>

        {/* Search and filters */}
        {enableGlobalSearch && (
          <div className="flex flex-col gap-3">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={globalSearchValue}
                onChange={(e) => handleGlobalSearchChange(e.target.value)}
                className="pl-9 pr-9 border-border/50 focus:border-border bg-background text-sm h-9"
              />
              {globalSearchValue && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                  onClick={() => handleGlobalSearchChange('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>

            {/* Filter and sort status */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-xs text-muted-foreground">
                {paginatedData.length.toLocaleString()} of {data.length.toLocaleString()} results
              </div>
              
              {(hasActiveFilters || hasActiveSorts) && (
                <div className="flex items-center gap-2">
                  {hasActiveFilters && (
                    <Badge variant="secondary" className="text-xs">
                      Filtered
                    </Badge>
                  )}
                  {hasActiveSorts && (
                    <Badge variant="secondary" className="text-xs">
                      Sorted
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      handleClearAllFilters();
                      handleClearAllSorts();
                    }}
                    className="h-6 text-xs px-2 text-muted-foreground hover:text-foreground"
                  >
                    Clear all
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render loading rows
  const renderLoadingRows = () => {
    return Array.from({ length: loadingRows }).map((_, index) => (
      <tr key={`loading-${index}`} className="animate-pulse border-b border-border/20">
        {visibleColumns.map((column) => (
          <td
            key={column.key}
            className="px-3 py-3 text-sm"
            style={{ width: columnWidths[column.key] || column.width }}
          >
            <div className="h-4 bg-muted/40 rounded"></div>
          </td>
        ))}
      </tr>
    ));
  };

  // Render error state
  if (error) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-destructive">Error</CardTitle>
          <CardDescription>{error}</CardDescription>
        </CardHeader>
        <CardContent>
          {onRetry && (
            <Button onClick={onRetry} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn("w-full", className)}>
      {/* Toolbar */}
      {renderToolbar()}

      {/* Table */}
      <div className="w-full overflow-hidden">
        <table className="w-full border-separate border-spacing-0 table-auto">
          {/* Enhanced header with Notion-style features */}
          {enableNotionStyle ? (
            <EnhancedTableHeader
              columns={columns}
              data={data}
              filterState={filterState}
              sortState={sortState}
              columnVisibility={columnVisibility}
              onColumnResize={handleColumnResize}
              onFilterChange={handleFilterChange}
              onSortChange={handleSortChange}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              onMultiSortToggle={(enabled) => setSortState(prev => ({ ...prev, multiSort: enabled }))}
              onClearAllSorts={handleClearAllSorts}
              onClearAllFilters={handleClearAllFilters}
              enableFiltering={enableFiltering}
              enableSorting={enableSorting}
              enableGlobalColumnMenu={enableColumnVisibility}
              className={headerClassName}
            />
          ) : (
            // Simple header
            <thead className="bg-muted/50 border-b border-border sticky top-0 z-10">
              <tr>
                {visibleColumns.map((column) => (
                  <th
                    key={column.key}
                    className={cn(
                      "px-3 py-3 text-center text-xs font-medium text-muted-foreground tracking-wider",
                      column.sticky && "sticky left-0 bg-muted/50 z-20",
                      headerClassName
                    )}
                    style={{ width: columnWidths[column.key] || column.width }}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
          )}

          {/* Table body */}
          <tbody>
            {isLoading ? (
              renderLoadingRows()
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="px-3 py-16 text-center">
                  {emptyState || (
                    <div className="text-center space-y-3">
                      <div className="text-muted-foreground text-sm">
                        {processedData.length === 0 && data.length > 0 
                          ? "No results found for the current filters"
                          : "No data available"
                        }
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {processedData.length === 0 && data.length > 0 
                          ? "Try adjusting your search or filters"
                          : "No results found for the current view"
                        }
                      </div>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              paginatedData.map((row, index) => (
                <tr
                  key={index}
                  className={cn(
                    getRowClassName(row, index),
                    onRowClick && "cursor-pointer"
                  )}
                  onClick={() => onRowClick?.(row, index)}
                >
                  {visibleColumns.map((column) => (
                    <td
                      key={column.key}
                      className={cn(
                        "px-3 py-3 text-sm break-words whitespace-normal",
                        column.sticky && "sticky left-0 bg-background z-10",
                        column.className
                      )}
                      style={{ 
                        width: columnWidths[column.key] || column.width,
                        minWidth: column.width ? `${parseInt(column.width.replace('px', '')) / 2}px` : '100px',
                        maxWidth: 'none'
                      }}
                    >
                      <div className="break-words whitespace-normal">
                        {cellRenderer(row[column.key], column, row, index)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginatedData.length > 0 && (
        <div className="px-6 py-4 border-t border-border/30">
          <PaginationControls
            paginationState={paginationState}
            onPaginationChange={setPaginationState}
            compact={compactMode}
          />
        </div>
      )}
    </div>
  );
}

// Export types for backward compatibility
export type { 
  EnhancedTableColumn as TableColumn,
  CellRenderer,
  TableStateConfig 
};

export default UnifiedTable;